import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/** Post-checkout thank-you page. Paddle redirects users here with the
 *  `plan` query param we injected in `successUrl`. We poll `/usage` for
 *  up to ~30s to detect when the webhook has provisioned the credits, so
 *  the user sees "Pagamento confermato" instead of a stale 0-credit
 *  state while Paddle's async notification is in flight. */
export default function BillingSuccess() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const plan = (sp.get("plan") || "").toLowerCase();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("pending"); // pending | confirmed | timeout
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const maxTries = 15; // ~30 s @ 2 s
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await api.get("/usage");
        const remaining = Number(r?.data?.remaining ?? 0);
        if (remaining > 0) {
          setCredits(remaining);
          setStatus("confirmed");
          refresh();
          return;
        }
      } catch {
        /* ignore */
      }
      if (tries >= maxTries) {
        setStatus("timeout");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] text-white">
      <Header />
      <main className="max-w-[720px] mx-auto px-6 py-20 text-center">
        <div
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-8"
          data-testid="billing-success-icon"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </div>
        <h1
          className="text-4xl sm:text-5xl font-display font-bold tracking-tight mb-4"
          data-testid="billing-success-title"
        >
          {t("billing_success.title")}
        </h1>
        <p className="text-base text-[color:var(--fg-muted)] mb-10 max-w-xl mx-auto">
          {plan
            ? t(`billing_success.subtitle_${plan}`, { defaultValue: t("billing_success.subtitle") })
            : t("billing_success.subtitle")}
        </p>

        <div
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 mb-10"
          data-testid="billing-success-status"
        >
          {status === "pending" && (
            <>
              <div className="text-xs tiny-caps text-amber-300 mb-2">
                {t("billing_success.waiting_kicker")}
              </div>
              <p className="text-sm text-white/80">
                {t("billing_success.waiting_body")}
              </p>
              <div className="mt-4 flex justify-center">
                <div className="w-10 h-1 rounded-full bg-amber-400/60 animate-pulse" />
              </div>
            </>
          )}
          {status === "confirmed" && (
            <>
              <div className="text-xs tiny-caps text-emerald-300 mb-2">
                {t("billing_success.confirmed_kicker")}
              </div>
              <p className="text-sm text-white/80 mb-2">
                {t("billing_success.confirmed_body", { credits })}
              </p>
              <div className="text-4xl font-display font-bold text-emerald-400 tabular-nums mt-3">
                {credits}
              </div>
            </>
          )}
          {status === "timeout" && (
            <>
              <div className="text-xs tiny-caps text-white/60 mb-2">
                {t("billing_success.timeout_kicker")}
              </div>
              <p className="text-sm text-white/80">
                {t("billing_success.timeout_body")}
              </p>
            </>
          )}
        </div>

        <Link
          to="/"
          data-testid="billing-success-cta"
          className="inline-flex items-center gap-2 bg-[color:var(--brand)] text-black font-semibold px-6 py-3 rounded-lg hover:bg-[color:var(--brand)]/90 transition-colors"
        >
          {t("billing_success.cta_start")}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
    </div>
  );
}
